import React, { useState, useEffect, useRef } from 'react';
import './Recovery.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../config/i18n';
import { Button, Form, InputGroup, Jumbotron, Navbar } from 'react-bootstrap';
import ToastComponent from '../Toast/Toast';
import { Redirect } from 'react-router-dom';
import { MdClose } from 'react-icons/md';
import { SecurityValidation } from '../SecurityValidation/SecurityValidation';
import { PRE, PRO } from '../../assets/constants';
import ProfileService from '../../services/Profile/ProfileService';
import { CustomModal } from '../CustomModal/CustomModal';

export const Recovery = () => {
  const { t } = useTranslation();

  const [oauthTokenB64, setOauthTokenB64] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showError, setShowError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [errorData, setErrorData] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [loadingChangePasswordProcess, setLoadingChangePasswordProcess] = useState(false);
  const [time, setTime] = useState(0);
  const [dataUpdated, setDataUpdated] = useState(false);
  const [showJumbotron, setShowJumbotron] = useState(true);
  const [isPasswordSecure, setIsPasswordSecure] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [newPasswordErrorText, setNewPasswordErrorText] = useState('');

  const timerRef = useRef(null);

  const changeLanguageHandler = (event) => {
    i18n.changeLanguage(event);
  };

  const doLogOut = () => {
    sessionStorage.clear();
    stopTimer();
    setRedirect(true);
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTime((prev) => prev + 1);
    }, 60000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const getOauthToken = () => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const token = url.searchParams.get('t');
    setOauthTokenB64(token);
  };

  const getViDMobileUserGUIDFromOauthToken = () => {
    if (oauthTokenB64 !== null) {
      const tokenParts = oauthTokenB64.split('.');
      const jsonTokenInfo = atob(tokenParts[1]);
      const tokenInfo = JSON.parse(jsonTokenInfo);
      return tokenInfo.sub;
    }
  };

  const getEnvironmentFromOauthToken = () => {
    if (oauthTokenB64 !== null) {
      const tokenParts = oauthTokenB64.split('.');
      const jsonTokenInfo = atob(tokenParts[1]);
      const tokenInfo = JSON.parse(jsonTokenInfo);
      return tokenInfo.env;
    }
  };

  const handlePasswordChange = () => {
    setLoadingChangePasswordProcess(true);
    if (password !== passwordConfirmation) {
      setNewPasswordErrorText(t('profile.password-error'));
      setNewPasswordError(true);
      setLoadingChangePasswordProcess(false);
    } else {
      updateUserPassword(password);
    }
  };

  const updateUserPassword = (pwd) => {
    const ViDMobileUserGUID = getViDMobileUserGUIDFromOauthToken();
    const ViDMobileUserEnvironment = getEnvironmentFromOauthToken();
    const env = ViDMobileUserEnvironment === 'PREPRODUCTION' ? PRE : PRO;
    const passwordBody = { password: pwd };

    ProfileService.updateUserPassword(
      ViDMobileUserGUID,
      passwordBody,
      oauthTokenB64,
      env
    )
      .then((res) => {
        setDataUpdated(true);
        setNewPasswordError(false);
        setLoadingChangePasswordProcess(false);
        setPassword('');
        setPasswordConfirmation('');
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          setSessionExpired(true);
          setLoadingChangePasswordProcess(false);
          startTimer();
        } else if (error.response) {
          setErrorType(error.response.status);
          setErrorData(error.response.data);
          setShowError(true);
          setLoadingChangePasswordProcess(false);
        }
      });
  };

  useEffect(() => {
    getOauthToken();
    const i18nextLng = localStorage.getItem('i18nextLng');
    if (i18nextLng !== null) {
      const lang = i18nextLng.split('-');
      changeLanguageHandler(lang[0]);
    } else {
      changeLanguageHandler('es');
    }
  }, []);

  if (redirect) {
    return <Redirect to={{ pathname: '/' }} />;
  }

  const language = localStorage.getItem('i18nextLng');
  const timeText =
    language === 'es'
      ? 'Hace ' + time + ' minutos'
      : language === 'en'
        ? time + ' minutes ago'
        : language === 'ca'
          ? 'Fa ' + time + ' minuts'
          : 'Hace ' + time + ' minutos';

  return (
    <div>
      <header className="App-header">
        <Navbar collapseOnSelect expand="lg" bg="light" variant="dark">
          <Navbar.Brand>
            <img
              src={require('../../assets/images/logo-vid-sit.png')}
              height="30"
              width="auto"
              alt="Validated ID logo"
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        </Navbar>
      </header>
      {dataUpdated && (
        <ToastComponent
          onClose={() => setDataUpdated(false)}
          show={dataUpdated}
          autohide
          delay={3000}
          title={t('profile.data-updated')}
          message={t('profile.data-updated-text')}
          variant="success"
          zIndex={10}
        />
      )}
      {sessionExpired && (
        <ToastComponent
          show={sessionExpired}
          title={t('general.notification')}
          subtitle={timeText}
          variant="session-expired"
          zIndex={10}
          closeButton={false}
        >
          <p>{t('general.session-expired')}</p>
          <Button
            type="button"
            className="btn btn-primary"
            id="sign-all-docs"
            onClick={() => doLogOut()}
          >
            {t('general.accept')}
          </Button>
        </ToastComponent>
      )}
      {showError && (
        <ToastComponent
          onClose={() => setShowError(false)}
          show={showError}
          title={
            <>
              {t('general.error')} {errorType}
            </>
          }
          message={errorData}
          variant="error"
          closeButton
          zIndex={15}
        />
      )}
      <CustomModal
        show={loadingChangePasswordProcess}
        handleHide={() => setLoadingChangePasswordProcess(false)}
        title={t('profile.update-process')}
        loading={true}
        closable={false}
      />
      {showJumbotron && (
        <Jumbotron>
          <MdClose
            className="close-jumbotron"
            onClick={() => setShowJumbotron(!showJumbotron)}
          />
          <div className="jumbotron-content">
            <h1>{t('recovery.recovery-title')}</h1>
            <p>{t('recovery.recovery-text')}</p>
          </div>
        </Jumbotron>
      )}
      <div className="recovery-container">
        {newPasswordError && (
          <div className="error-container">{newPasswordErrorText}</div>
        )}
        <InputGroup className="mb-3">
          <InputGroup.Prepend>
            <InputGroup.Text id="inputGroup-sizing-default">
              {t('recovery.recovery-new-password')}
            </InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            type="password"
            aria-label="Default"
            aria-describedby="inputGroup-sizing-default"
            value={password || ''}
            onChange={(e) => {
              setPassword(e.target.value);
              setNewPasswordError(false);
            }}
          />
        </InputGroup>
        <InputGroup className="mb-3">
          <InputGroup.Prepend>
            <InputGroup.Text id="inputGroup-sizing-default">
              {t('recovery.recovery-new-password-confirmation')}
            </InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            type="password"
            aria-label="Default"
            aria-describedby="inputGroup-sizing-default"
            value={passwordConfirmation || ''}
            onChange={(e) => {
              setPasswordConfirmation(e.target.value);
              setNewPasswordError(false);
            }}
          />
        </InputGroup>
        <SecurityValidation
          password={password}
          updateIsPasswordSecure={(isSecure) => setIsPasswordSecure(isSecure)}
        />
        <Button
          disabled={!isPasswordSecure || password !== passwordConfirmation}
          type="button"
          id="handlePasswordChange"
          className="btn btn-primary"
          onClick={() => handlePasswordChange()}
        >
          {t('general.confirm')}
        </Button>
      </div>
    </div>
  );
};

export default Recovery;
